import tw from "twin.macro";
import { Fragment } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CommandTrackingView } from "@/types/command-tracking.type";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";
import { useSendCommand } from "@/api/areas/mutations";
import { useAreaDataProvider } from "@/routes/area-aggregate/components/area-data-loader";
import { isEmergencyStopActive } from "../utils/is-emergency-stop";
import EmergencyStopSupportMessage from "../emergency-stop-support-message";
import EmergencyStopButton from "../emergency-stop-button";

function EmergencyStopSheetBody({
  areaId,
  areaCommandTracking,
}: {
  areaId: string;
  areaCommandTracking: CommandTrackingView;
}) {
  const { t: translator } = useTranslation();
  const { mutateAsync: sendCommand, isPending } = useSendCommand({ areaId });
  const { applyOptimisticCommand } = useAreaDataProvider();

  const isConfirmed = isEmergencyStopActive(areaCommandTracking);

  return (
    <div tw="flex flex-col items-stretch gap-4 p-4 pt-0">
      {isConfirmed ? (
        <Fragment>
          <EmergencyStopSupportMessage />

          {/* Rafraîchir : refresh complet du front. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            css={[
              tw`w-full flex items-center justify-center gap-2 m-1 min-h-[40px] rounded-3xl p-1`,
              tw`border border-gray-300 bg-transparent text-gray-500`,
              tw`active:bg-gray-200 transition transform duration-100`,
            ]}
          >
            <RefreshCcw tw="h-5 w-5" />
            <span tw="text-lg font-normal">
              {translator("modulesActions.REFRESH")}
            </span>
          </button>
        </Fragment>
      ) : (
        <Fragment>
          <div tw="flex flex-col items-center gap-3 py-6">
            <Loader2 tw="h-10 w-10 text-modules-redBase animate-spin" />
            <p tw="text-center text-lg font-medium text-gray-700">
              {translator("action.emergencyStopInProgress")}
            </p>
          </div>

          {/* Bouton interne "sans progress" : renvoi rapide de PAUSE tant que
              la gateway n'a pas acquitté. */}
          <EmergencyStopButton
            disabled={isPending}
            label={translator("action.emergencyStop")}
            onClick={() => {
              try {
                applyOptimisticCommand(ModuleCommandEnum.PAUSE);
                sendCommand({ command: ModuleCommandEnum.PAUSE });
              } catch (error) {
                toast.error("Erreur lors de l'arrêt d'urgence");
              }
            }}
          />
        </Fragment>
      )}
    </div>
  );
}

export default EmergencyStopSheetBody;
